import type { KieGeneralModelConfig } from '../types';
import { extractImageFrames, clampTo } from '../../tools';

export const googleConfigs: Record<string, KieGeneralModelConfig> = {
    'veo3.1-lite': {
        // https://docs.kie.ai/cn/veo3-api/generate-veo-3-video
        apiPath: '/api/v1/veo/generate',
        pollApiPath: '/api/v1/veo/record-info',
        statusMapping: {
            taskIdPath: 'data.taskId',
            success: [1, 4],
            failed: [2, 3],
            processing: [0],
            statePath: 'data.successFlag',
            videoUrlPath: 'data.response.resultUrls',
            errorPath: 'data.errorMessage',
        },
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, duration, aspectRatio, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);
            // 首帧模式
            if (frameMode === 'first') {
                return {
                    model: 'veo3_lite',
                    prompt: prompt,
                    aspectRatio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                    duration: clampTo(duration || 8, [4, 6, 8] as const, 8),
                    resolution: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p'),
                    // 首帧 URL
                    imageUrls: [firstImage!],
                    // 可选参数
                    waterMark: typeof providerOptions?.waterMark === 'string' ? providerOptions.waterMark : undefined,
                    // system
                    generationType: 'FIRST_AND_LAST_FRAMES_2_VIDEO',
                    callBackUrl: undefined,
                    enableTranslation: false,

                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                return {
                    model: 'veo3_lite',
                    prompt: prompt,
                    aspectRatio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                    duration: clampTo(duration || 8, [4, 6, 8] as const, 8),
                    resolution: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p'),
                    // 首尾帧 URL
                    imageUrls: [firstImage!, lastImage!],
                    // 可选参数
                    waterMark: typeof providerOptions?.waterMark === 'string' ? providerOptions.waterMark : undefined,
                    // system
                    generationType: 'FIRST_AND_LAST_FRAMES_2_VIDEO',
                    callBackUrl: undefined,
                    enableTranslation: false,
                }
            // 文生视频模式
            } else {
                return {
                    model: 'veo3_lite',
                    prompt: prompt,
                    aspectRatio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                    duration: clampTo(duration || 8, [4, 6, 8] as const, 8),
                    resolution: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p'),
                    // 可选参数
                    waterMark: typeof providerOptions?.waterMark === 'string' ? providerOptions.waterMark : undefined,
                    // system
                    generationType: 'TEXT_2_VIDEO',
                    callBackUrl: undefined,
                    enableTranslation: false,
                }
            }
        }
    },
    'veo3.1-fast': {
        // https://docs.kie.ai/cn/veo3-api/generate-veo-3-video
        apiPath: '/api/v1/veo/generate',
        pollApiPath: '/api/v1/veo/record-info',
        statusMapping: {
            taskIdPath: 'data.taskId',
            success: [1, 4],
            failed: [2, 3],
            processing: [0],
            statePath: 'data.successFlag',
            videoUrlPath: 'data.response.resultUrls',
            errorPath: 'data.errorMessage',
        },
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, duration, aspectRatio, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);
            if (frameMode === 'first') {
                return {
                    model: 'veo3_fast',
                    prompt: prompt,
                    aspectRatio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                    duration: clampTo(duration || 8, [4, 6, 8] as const, 8),
                    resolution: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p'),
                    // 首帧 URL
                    imageUrls: [firstImage!],
                    // 可选参数
                    waterMark: typeof providerOptions?.waterMark === 'string' ? providerOptions.waterMark : undefined,
                    // system
                    generationType: 'FIRST_AND_LAST_FRAMES_2_VIDEO',
                    callBackUrl: undefined,
                    enableTranslation: false,
                };
            } else if (frameMode === 'first_last') {
                return {
                    model: 'veo3_fast',
                    prompt: prompt,
                    aspectRatio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                    duration: clampTo(duration || 8, [4, 6, 8] as const, 8),
                    resolution: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p'),
                    // 首帧 URL
                    imageUrls: [firstImage!, lastImage!],
                    // 可选参数
                    waterMark: typeof providerOptions?.waterMark === 'string' ? providerOptions.waterMark : undefined,
                    // system
                    generationType: 'FIRST_AND_LAST_FRAMES_2_VIDEO',
                    callBackUrl: undefined,
                    enableTranslation: false,
                }
            } else if (frameMode === 'ref') {
                return {
                    model: 'veo3_fast',
                    prompt: prompt,
                    aspectRatio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                    duration: clampTo(duration || 8, [4, 6, 8] as const, 8),
                    resolution: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p'),
                    // 参考帧 URL
                    imageUrls: imagePrompt!.slice(0, 3),
                    // 可选参数
                    waterMark: typeof providerOptions?.waterMark === 'string' ? providerOptions.waterMark : undefined,
                    // system
                    generationType: 'REFERENCE_2_VIDEO',
                    callBackUrl: undefined,
                    enableTranslation: false,
                }
            // 文生视频模式
            } else {
                return {
                    model: 'veo3_fast',
                    prompt: prompt,
                    aspectRatio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                    duration: clampTo(duration || 8, [4, 6, 8] as const, 8),
                    resolution: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p'),
                    // 可选参数
                    waterMark: typeof providerOptions?.waterMark === 'string' ? providerOptions.waterMark : undefined,
                    // system
                    generationType: 'TEXT_2_VIDEO',
                    callBackUrl: undefined,
                    enableTranslation: false,
                }
            }
        }
    },
    'veo3.1': {
        // https://docs.kie.ai/cn/veo3-api/generate-veo-3-video
        apiPath: '/api/v1/veo/generate',
        pollApiPath: '/api/v1/veo/record-info',
        statusMapping: {
            taskIdPath: 'data.taskId',
            success: [1, 4],
            failed: [2, 3],
            processing: [0],
            statePath: 'data.successFlag',
            videoUrlPath: 'data.response.resultUrls',
            errorPath: 'data.errorMessage',
        },
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, duration, aspectRatio, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);
            // 首帧模式
            if (frameMode === 'first') {
                return {
                    model: 'veo3',
                    prompt: prompt,
                    aspectRatio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                    duration: clampTo(duration || 8, [4, 6, 8] as const, 8),
                    resolution: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p'),
                    // 首帧 URL
                    imageUrls: [firstImage!],
                    // 可选参数
                    waterMark: typeof providerOptions?.waterMark === 'string' ? providerOptions.waterMark : undefined,
                    // system
                    generationType: 'FIRST_AND_LAST_FRAMES_2_VIDEO',
                    callBackUrl: undefined,
                    enableTranslation: false,

                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                return {
                    model: 'veo3',
                    prompt: prompt,
                    aspectRatio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                    duration: clampTo(duration || 8, [4, 6, 8] as const, 8),
                    resolution: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p'),
                    // 首尾帧 URL
                    imageUrls: [firstImage!, lastImage!],
                    // 可选参数
                    waterMark: typeof providerOptions?.waterMark === 'string' ? providerOptions.waterMark : undefined,
                    // system
                    generationType: 'FIRST_AND_LAST_FRAMES_2_VIDEO',
                    callBackUrl: undefined,
                    enableTranslation: false,
                }
            // 文生视频模式
            } else {
                return {
                    model: 'veo3',
                    prompt: prompt,
                    aspectRatio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                    duration: clampTo(duration || 8, [4, 6, 8] as const, 8),
                    resolution: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p'),
                    // 可选参数
                    waterMark: typeof providerOptions?.waterMark === 'string' ? providerOptions.waterMark : undefined,
                    // system
                    generationType: 'TEXT_2_VIDEO',
                    callBackUrl: undefined,
                    enableTranslation: false,
                }
            }
        }
    }
};